import { useEffect, useState, type SubmitEvent } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useEmbeddingModels } from "../hooks/use-embedding-models";
import { useCreateKnowledgeBase } from "../hooks/use-create-knowledge-base";
import type { CreateKnowledgeBaseInput } from "../../domain/entities/knowledge-base";

const DEFAULT_CHUNK_SIZE = 128;
const DEFAULT_CHUNK_OVERLAP = 0.2;

type Props = {
  open: boolean;
  onClose: () => void;
};

export const CreateKbDialog = ({ open, onClose }: Props) => {
  const { data: embeddingModels = [] } = useEmbeddingModels();
  const { mutate: create, isPending } = useCreateKnowledgeBase();

  const [name, setName] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [chunkSize, setChunkSize] = useState(DEFAULT_CHUNK_SIZE);
  const [chunkOverlap, setChunkOverlap] = useState(DEFAULT_CHUNK_OVERLAP);
  const [embeddingModel, setEmbeddingModel] = useState<string>("");

  const defaultEmbeddingCode = embeddingModels[0]?.value ?? "";
  useEffect(() => {
    if (!open || !defaultEmbeddingCode) return;
    setEmbeddingModel((prev) => (prev === "" ? defaultEmbeddingCode : prev));
  }, [open, defaultEmbeddingCode]);

  const resetForm = () => {
    setName("");
    setAdvancedOpen(false);
    setChunkSize(DEFAULT_CHUNK_SIZE);
    setChunkOverlap(DEFAULT_CHUNK_OVERLAP);
    setEmbeddingModel(embeddingModels[0]?.value ?? "");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;

    const input: CreateKnowledgeBaseInput = {
      name: name.trim(),
      chunkingConfig: {
        chunkSize: chunkSize,
        chunkOverlap,
        embeddingModelCode: embeddingModel,
      },
    };
    create(input, { onSuccess: handleClose });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create new Knowledge Base</DialogTitle>
        </DialogHeader>

        <form
          id="create-kb-form"
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="kb-name">Name *</Label>
            <Input
              id="kb-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Knowledge Base"
              required
              disabled={isPending}
            />
          </div>

          <div>
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              {advancedOpen ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
              Advanced configuration
            </button>

            {advancedOpen && (
              <div className="mt-3 flex flex-col gap-4 rounded-lg border p-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="kb-chunk-size">Chunk size</Label>
                  <Input
                    id="kb-chunk-size"
                    type="number"
                    min={1}
                    step={1}
                    value={chunkSize}
                    onChange={(e) => setChunkSize(parseInt(e.target.value, 10) || 0)}
                    placeholder="128"
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    Detail chunk size in tokens, e.g. 128
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="kb-chunk-overlap">Chunk overlap</Label>
                  <Input
                    id="kb-chunk-overlap"
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={chunkOverlap}
                    onChange={(e) =>
                      setChunkOverlap(parseFloat(e.target.value))
                    }
                    disabled={isPending}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="kb-embedding-model">Embedding model</Label>
                  <select
                    id="kb-embedding-model"
                    value={embeddingModel}
                    onChange={(e) => setEmbeddingModel(e.target.value)}
                    disabled={isPending || embeddingModels.length === 0}
                    className={cn(
                      "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
                      "focus:ring-1 focus:ring-ring",
                      "disabled:cursor-not-allowed disabled:opacity-50",
                    )}
                  >
                    {embeddingModels.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.display}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-kb-form"
            disabled={isPending || !name.trim()}
          >
            {isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
